import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, DownloadSimple, Sparkle, SpinnerGap, EnvelopeSimple, TelegramLogo } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const FYClosingReport = () => {
  const [availableFYs, setAvailableFYs] = useState([]);
  const [selectedFY, setSelectedFY] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [fetchingFYs, setFetchingFYs] = useState(true);

  useEffect(() => {
    const fetchFYs = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/export/available-fys`, { withCredentials: true });
        setAvailableFYs(data);
        if (data.length > 0) setSelectedFY(data[0].financial_year);
      } catch (err) { console.error(err.message); }
      finally { setFetchingFYs(false); }
    };
    fetchFYs();
  }, []);

  const handleDownload = async () => {
    if (!selectedFY) { toast.error('Select a financial year'); return; }
    setLoading(true);
    toast.info('Generating FY report with AI commentary... This may take 15-20 seconds.');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/fy-report`, {
        params: { fy: selectedFY },
        withCredentials: true,
        responseType: 'blob',
        timeout: 60000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedFY.replace(' ', '_')}_closing_report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('FY closing report downloaded');
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailReport = async () => {
    if (!selectedFY) { toast.error('Select a financial year'); return; }
    setEmailing(true);
    toast.info('Generating & emailing FY report to your team... This may take 20-30 seconds.');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/export/fy-report/email`,
        { fy: selectedFY },
        { withCredentials: true, timeout: 90000 }
      );
      toast.success(data.message || 'FY report emailed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to email report');
    } finally {
      setEmailing(false);
    }
  };

  const handleTelegram = async () => {
    if (!selectedFY) { toast.error('Select a financial year'); return; }
    setSendingTelegram(true);
    toast.info('Generating & sending FY report via Telegram...');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/telegram/send-report`,
        { report_type: 'fy', fy: selectedFY },
        { withCredentials: true, timeout: 90000 }
      );
      toast.success(data.message || 'FY report sent via Telegram');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send via Telegram');
    } finally {
      setSendingTelegram(false);
    }
  };

  const selected = availableFYs.find(f => f.financial_year === selectedFY);

  if (fetchingFYs) return null;
  if (availableFYs.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="fy-closing-report">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center">
          <FileText size={20} weight="fill" className="text-primary" />
        </div>
        <div>
          <div className="text-lg font-semibold tracking-tight">FY Closing Report</div>
          <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">COMPREHENSIVE PDF WITH AI COMMENTARY</div>
        </div>
      </div>

      <div className="flex items-end gap-4 flex-wrap">
        <div className="space-y-1.5">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Financial Year</div>
          <Select value={selectedFY} onValueChange={setSelectedFY}>
            <SelectTrigger className="w-[200px] rounded-sm" data-testid="fy-report-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableFYs.map(f => (
                <SelectItem key={f.financial_year} value={f.financial_year}>
                  {f.financial_year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selected && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>₹{selected.total_revenue.toLocaleString()}</span>
            <span>{selected.total_transactions.toLocaleString()} transactions</span>
          </div>
        )}

        <Button
          onClick={handleDownload}
          disabled={loading || emailing || !selectedFY}
          className="rounded-sm"
          data-testid="download-fy-report-button"
        >
          {loading ? (
            <>
              <SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <DownloadSimple size={16} weight="bold" className="mr-2" />
              Download PDF
            </>
          )}
        </Button>

        <Button
          onClick={handleEmailReport}
          disabled={emailing || loading || !selectedFY}
          variant="outline"
          className="rounded-sm"
          data-testid="email-fy-report-button"
        >
          {emailing ? (
            <>
              <SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <EnvelopeSimple size={16} weight="bold" className="mr-2" />
              Email to Team
            </>
          )}
        </Button>

        <Button
          onClick={handleTelegram}
          disabled={sendingTelegram || loading || emailing || !selectedFY}
          variant="outline"
          className="rounded-sm text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/20"
          data-testid="telegram-fy-report-button"
        >
          {sendingTelegram ? (
            <>
              <SpinnerGap size={16} weight="bold" className="mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <TelegramLogo size={16} weight="fill" className="mr-2" />
              Telegram
            </>
          )}
        </Button>
      </div>

      <div className="mt-4 text-xs text-muted-foreground space-y-1">
        <div className="flex items-center gap-1.5">
          <Sparkle size={12} weight="fill" className="text-primary" />
          Includes AI-powered executive commentary, monthly breakdown, client rankings, and industry analysis
        </div>
        <div className="flex items-center gap-1.5">
          <EnvelopeSimple size={12} weight="regular" className="text-muted-foreground" />
          Auto-sent to all team members on March 31st at 11:30 PM IST
        </div>
      </div>
    </div>
  );
};

export default FYClosingReport;
